import type { LocaleCode, Translate } from '@client-app/i18n';
import dayjs from 'dayjs';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const formatPlanDate = (dateStr: string, locale: LocaleCode = 'en'): string => {
    const date = new Date(dateStr);
    if (!Number.isNaN(date.getTime())) {
        return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: locale === 'zh-CN' ? 'numeric' : 'short',
            day: 'numeric',
        }).format(date);
    }

    return dayjs(dateStr).format('MMM D, YYYY');
};

export const formatAutoRenewText = (
    renewsAt?: string | null,
    locale: LocaleCode = 'en',
    t?: Translate,
): string => {
    if (!renewsAt) return '';
    const date = formatPlanDate(renewsAt, locale);
    return t
        ? t('website.planText.autoRenewsOn', { date })
        : `Your plan auto-renews on ${date}.`;
};

export const getRemainingDays = (endDate: string, now: Date = new Date()): number => {
    const endTimestamp = new Date(endDate).getTime();
    if (!Number.isFinite(endTimestamp)) return 0;
    return Math.max(0, Math.ceil((endTimestamp - now.getTime()) / MS_PER_DAY));
};

export const formatTrialExpirationText = (
    trialEndsAt?: string | null,
    now: Date = new Date(),
    locale: LocaleCode = 'en',
    t?: Translate,
): string => {
    if (!trialEndsAt) return '';

    const remainingDays = getRemainingDays(trialEndsAt, now);

    if (remainingDays <= 0) {
        return t
            ? t('website.planText.trialEndsToday')
            : 'Free trial ends today.';
    }

    if (remainingDays === 1) {
        return t
            ? t('website.planText.oneDayLeftInTrial')
            : 'Free trial ends in 1 day.';
    }

    return t
        ? t('website.planText.daysLeftInTrial', { days: remainingDays })
        : `Free trial ends in ${remainingDays} days.`;
};
